import React, { useState, useEffect } from 'react';
import { Users, ShoppingCart, MessageCircle, Download, Filter, RefreshCw, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_LABELS = {
  basic_plan: 'Basic Plan',
  pro_plan: 'Pro Plan',
  premium_plan: 'Premium Plan',
  consulting: 'Online-Beratung',
  personal_training: 'Personal Training',
  pdf_guide: 'PDF-Guide'
};

const SERVICE_COLORS = {
  basic_plan: '#4CAF50',
  pro_plan: '#2196F3',
  premium_plan: '#9C27B0',
  consulting: '#FF9800',
  personal_training: '#E91E63',
  pdf_guide: '#00BCD4'
};

export const AdminDashboard = () => {
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [interactionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/interactions${filter ? `?serviceType=${filter}` : ''}`),
        axios.get(`${API}/interactions/stats`)
      ]);
      setInteractions(interactionsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-main)',
      padding: 'var(--spacing-large)'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 'var(--spacing-xl)',
          flexWrap: 'wrap',
          gap: 'var(--spacing-medium)'
        }}>
          <div>
            <Link to="/" style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '8px',
              color: 'var(--brand-dark)',
              textDecoration: 'none',
              marginBottom: 'var(--spacing-small)'
            }}>
              <ArrowLeft size={20} />
              Zurück zur Webseite
            </Link>
            <h1 className="display-small" style={{ color: 'var(--brand-dark)' }}>
              Admin Dashboard
            </h1>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              Alle Kundenanfragen und Käufe im Überblick
            </p>
          </div>
          <button 
            onClick={fetchData}
            className="btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            data-testid="refresh-button"
          >
            <RefreshCw size={18} />
            Aktualisieren
          </button>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--spacing-medium)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div className="network-card" style={{ cursor: 'default', textAlign: 'center' }} data-testid="stats-total">
            <Users size={32} style={{ color: 'var(--brand-dark)', marginBottom: 'var(--spacing-small)' }} />
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-dark)' }}>
              {stats.total || 0}
            </div>
            <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>Gesamt</div>
          </div>
          
          <div className="network-card" style={{ cursor: 'default', textAlign: 'center' }} data-testid="stats-plans">
            <ShoppingCart size={32} style={{ color: '#4CAF50', marginBottom: 'var(--spacing-small)' }} />
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-dark)' }}>
              {(stats.basic_plan || 0) + (stats.pro_plan || 0) + (stats.premium_plan || 0)}
            </div>
            <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>Programm-Käufe</div>
          </div>
          
          <div className="network-card" style={{ cursor: 'default', textAlign: 'center' }} data-testid="stats-consulting">
            <MessageCircle size={32} style={{ color: '#FF9800', marginBottom: 'var(--spacing-small)' }} />
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-dark)' }}>
              {(stats.consulting || 0) + (stats.personal_training || 0)}
            </div>
            <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>Beratung & PT</div>
          </div>
          
          <div className="network-card" style={{ cursor: 'default', textAlign: 'center' }} data-testid="stats-pdf">
            <Download size={32} style={{ color: '#00BCD4', marginBottom: 'var(--spacing-small)' }} />
            <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-dark)' }}>
              {stats.pdf_guide || 0}
            </div>
            <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>PDF-Guide</div>
          </div>
        </div>

        {/* Filter */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'var(--spacing-medium)',
          marginBottom: 'var(--spacing-large)',
          flexWrap: 'wrap'
        }}>
          <Filter size={20} style={{ color: 'var(--text-secondary)' }} />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '12px 20px',
              borderRadius: '12px',
              border: '2px solid var(--border-medium)',
              fontSize: '1rem',
              background: 'white',
              cursor: 'pointer',
              minWidth: '200px'
            }}
            data-testid="filter-select"
          >
            <option value="">Alle anzeigen</option>
            <option value="basic_plan">Basic Plan</option>
            <option value="pro_plan">Pro Plan</option>
            <option value="premium_plan">Premium Plan</option>
            <option value="consulting">Online-Beratung</option>
            <option value="personal_training">Personal Training</option>
            <option value="pdf_guide">PDF-Guide</option>
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: 'var(--spacing-medium)',
            borderRadius: '12px',
            marginBottom: 'var(--spacing-large)'
          }} data-testid="error-message">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid var(--border-medium)',
              borderTopColor: 'var(--brand-dark)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
            <p className="body-medium" style={{ marginTop: 'var(--spacing-medium)', color: 'var(--text-secondary)' }}>
              Laden...
            </p>
          </div>
        )}

        {/* Interactions Table */}
        {!loading && interactions.length === 0 && (
          <div className="network-card" style={{ textAlign: 'center', cursor: 'default' }} data-testid="no-data">
            <Users size={48} style={{ color: 'var(--text-light)', marginBottom: 'var(--spacing-medium)' }} />
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Keine Anfragen gefunden.
            </p>
          </div>
        )}

        {!loading && interactions.length > 0 && (
          <div className="network-card" style={{ cursor: 'default', overflow: 'hidden', padding: 0 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="interactions-table">
                <thead>
                  <tr style={{ background: 'var(--bg-subtle)' }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-medium)' }}>Datum</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-medium)' }}>Service</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-medium)' }}>Name</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-medium)' }}>E-Mail</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-medium)' }}>Telefon</th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--border-medium)' }}>Standort</th>
                  </tr>
                </thead>
                <tbody>
                  {interactions.map((interaction, index) => (
                    <tr 
                      key={interaction.id || index}
                      style={{ 
                        borderBottom: '1px solid var(--border-light)',
                        background: index % 2 === 0 ? 'white' : 'var(--bg-subtle)'
                      }}
                      data-testid={`interaction-row-${index}`}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        {formatDate(interaction.timestamp)}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          background: SERVICE_COLORS[interaction.serviceType] || '#999',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: 500
                        }}>
                          {SERVICE_LABELS[interaction.serviceType] || interaction.serviceType}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 500 }}>
                        {interaction.firstName} {interaction.lastName}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <a href={`mailto:${interaction.email}`} style={{ color: 'var(--brand-dark)' }}>
                          {interaction.email}
                        </a>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {interaction.phone ? (
                          <a href={`tel:${interaction.phone}`} style={{ color: 'var(--brand-dark)' }}>
                            {interaction.phone}
                          </a>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {interaction.location || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};
